import React, { createContext, useContext, useEffect, useMemo } from "react";

import { GET_ALL_PATIENT_USERS_IN_ORG, GetAllPatientUsersResponse } from "@/utils/GraphQLClient/patient-user";
import { FhirResourceType, extractFhirResourceId } from "@/utils/fhir-utils";

import * as Sentry from "@sentry/react";
import { useQuery } from "urql";

/**
 * Value provided by {@link PatientVitallUserProvider}.
 *
 * @property fhirPatientIdToUserIdMap - Maps FHIR Patient resource IDs to Vitall user IDs.
 * Used for:
 * - Messenger: Look up a patient's chat user so unread message counts can be resolved
 * - Titration: Look up a patient's features to see if titration is enabled
 * @property isLoading - `true` while the GraphQL query is in flight.
 */
interface PatientVitallUserContextValue {
  fhirPatientIdToUserIdMap: Map<string, string>;
  isLoading: boolean;
}

const PatientVitallUserContext = createContext<PatientVitallUserContextValue>({
  fhirPatientIdToUserIdMap: new Map(),
  isLoading: true,
});

/**
 * Fetches all patient-user mappings for the organization via GraphQL and
 * exposes them through {@link usePatientVitallUserContext}.
 *
 * The map is built once on mount (and updated if the query result changes).
 * Each entry maps a FHIR `Patient` resource ID to the corresponding Vitall
 * user ID, enabling downstream components to resolve chat room JIDs and
 * display per-patient unread message counts.
 */
export const PatientVitallUserProvider = ({ children }: { children: React.ReactNode }) => {
  const [{ data, error, fetching }] = useQuery<{
    getAllPatientUsers: GetAllPatientUsersResponse[];
  }>({
    query: GET_ALL_PATIENT_USERS_IN_ORG,
  });

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        level: "error",
        tags: {
          feature: "patient-user-context",
          operation: "get-all-patient-users",
        },
      });
    }
  }, [error]);

  const value = useMemo(() => {
    const fhirPatientIdToUserIdMap = new Map<string, string>();
    (data?.getAllPatientUsers ?? []).forEach(u => {
      const fhir_patient_id = extractFhirResourceId(u.vitall_patient?.patient_id, FhirResourceType.Patient);
      if (fhir_patient_id) {
        fhirPatientIdToUserIdMap.set(fhir_patient_id, u.user_id);
      }
    });
    return {
      isLoading: fetching,
      fhirPatientIdToUserIdMap,
    };
  }, [data, fetching]);

  return <PatientVitallUserContext.Provider value={value}>{children}</PatientVitallUserContext.Provider>;
};

/** Convenience hook to consume the patient-user mapping from {@link PatientVitallUserProvider}. */
export const usePatientVitallUserContext = (): PatientVitallUserContextValue => useContext(PatientVitallUserContext);
